import { listNotifications, markAllNotificationsRead, markNotificationRead } from '~/api/notice'
import type { NotificationCategoryFilterDto, NotificationUnreadDto } from '~/types/api/notice'
import type { NoticeFilter, NoticeItem } from '~/pages/me/notice/types'
import { emptyNoticeUnread, mapNotificationItem } from '~/pages/me/notice/utils/map'

function toApiCategory(filter: NoticeFilter): NotificationCategoryFilterDto {
  return filter || 'all'
}

const UNREAD_KEY = 'me-notice-unread'
const LIST_KEY = 'me-notices'
const LOADING_KEY = 'me-notices-loading'
const LOADING_MORE_KEY = 'me-notices-loading-more'
const FILTER_KEY = 'me-notices-filter'
const CURSOR_KEY = 'me-notices-cursor'
const HAS_MORE_KEY = 'me-notices-has-more'

/** 并发 refresh 序号：只应用最后一次结果，避免快切页签写乱 */
let refreshSeq = 0

function useNoticeUnreadState() {
  return useState<NotificationUnreadDto>(UNREAD_KEY, () => emptyNoticeUnread())
}

function useNoticeListState() {
  return useState<NoticeItem[]>(LIST_KEY, () => [])
}

/** /me.unread.notice → 顶栏徽标（可在插件中调用） */
export function setNoticeUnreadFromMe(count: number) {
  const userStore = useUserStore()
  const unread = useNoticeUnreadState()
  const n = Math.max(0, Math.floor(count) || 0)
  unread.value = { ...unread.value, all: n }
  userStore.noticeUnread = n
}

/** 退出 / 401 清站内信缓存（由 session 插件统一触发） */
export function resetNotices() {
  refreshSeq += 1
  const userStore = useUserStore()
  useNoticeListState().value = []
  useNoticeUnreadState().value = emptyNoticeUnread()
  useState(LOADING_KEY, () => false).value = false
  useState(LOADING_MORE_KEY, () => false).value = false
  useState(CURSOR_KEY, () => '').value = ''
  useState(HAS_MORE_KEY, () => false).value = false
  useState<NoticeFilter>(FILTER_KEY, () => '').value = ''
  userStore.noticeUnread = 0
}

/** 站内信：列表 + 页签未读；顶栏徽标以 userStore.noticeUnread 为准 */
export const useNotices = () => {
  const userStore = useUserStore()

  const notices = useNoticeListState()
  const unread = useNoticeUnreadState()
  const loading = useState(LOADING_KEY, () => false)
  const loadingMore = useState(LOADING_MORE_KEY, () => false)
  const loadedFilter = useState<NoticeFilter>(FILTER_KEY, () => '')
  const cursor = useState(CURSOR_KEY, () => '')
  const hasMore = useState(HAS_MORE_KEY, () => false)

  const unreadTotal = computed(() => unread.value.all)

  const applyUnread = (next: NotificationUnreadDto) => {
    unread.value = { ...next }
    userStore.noticeUnread = next.all
  }

  /** 切换页签瞬间：用当前列表按分类兜底，避免整页闪空 */
  const filtered = (cat: NoticeFilter) => {
    if (loadedFilter.value === cat) return notices.value
    return notices.value.filter(n => !cat || n.cat === cat)
  }

  const applyPage = (items: NoticeItem[], nextCursor: string, more: boolean, append: boolean) => {
    if (!append) {
      notices.value = items
    } else {
      const seen = new Set(notices.value.map(row => row.id))
      const fresh = items.filter(row => !seen.has(row.id))
      if (!fresh.length) {
        cursor.value = ''
        hasMore.value = false
        return
      }
      notices.value = notices.value.concat(fresh)
    }
    cursor.value = nextCursor
    hasMore.value = more && Boolean(nextCursor)
  }

  /** GET /notifications；按页签拉第一页 */
  const refresh = async (filter: NoticeFilter = '') => {
    if (!userStore.isLoggedIn) {
      resetNotices()
      return
    }
    const seq = ++refreshSeq
    loading.value = true
    loadingMore.value = false
    cursor.value = ''
    hasMore.value = false
    try {
      const res = await listNotifications({ category: toApiCategory(filter) })
      if (seq !== refreshSeq) return
      applyPage(
        (res.items ?? []).map(mapNotificationItem),
        res.next_cursor ?? '',
        Boolean(res.has_more),
        false,
      )
      applyUnread(res.unread ?? emptyNoticeUnread())
      loadedFilter.value = filter
    } finally {
      if (seq === refreshSeq) loading.value = false
    }
  }

  /** 滑到底用上一页 next_cursor 继续拉，原样回传 */
  const loadMore = async () => {
    if (!userStore.isLoggedIn) return
    if (loading.value || loadingMore.value || !hasMore.value || !cursor.value) return
    const seq = refreshSeq
    const requestCursor = cursor.value
    const filter = loadedFilter.value
    loadingMore.value = true
    try {
      const res = await listNotifications({
        category: toApiCategory(filter),
        cursor: requestCursor,
      })
      if (seq !== refreshSeq) return
      applyPage(
        (res.items ?? []).map(mapNotificationItem),
        res.next_cursor ?? '',
        Boolean(res.has_more),
        true,
      )
      applyUnread(res.unread ?? emptyNoticeUnread())
    } finally {
      if (seq === refreshSeq) loadingMore.value = false
    }
  }

  const markRead = async (id: string) => {
    const row = notices.value.find(n => n.id === id)
    if (row && !row.unread) return
    if (row) row.unread = false
    try {
      const res = await markNotificationRead(id)
      applyUnread(res.unread ?? emptyNoticeUnread())
    } catch (error) {
      if (row) row.unread = true
      throw error
    }
  }

  const markAllRead = async () => {
    if (unread.value.all <= 0) return false
    const snapshot = notices.value.map(n => ({ id: n.id, unread: n.unread }))
    notices.value.forEach(n => {
      n.unread = false
    })
    try {
      const res = await markAllNotificationsRead({ category: 'all' })
      applyUnread(res.unread ?? emptyNoticeUnread())
      return true
    } catch (error) {
      const map = new Map(snapshot.map(s => [s.id, s.unread]))
      notices.value.forEach(n => {
        n.unread = map.get(n.id) ?? n.unread
      })
      throw error
    }
  }

  return {
    unread,
    loading,
    loadingMore,
    hasMore,
    unreadTotal,
    filtered,
    refresh,
    loadMore,
    markRead,
    markAllRead,
  }
}
